import { supabase } from './supabase';
import type {
  ReframingPracticeMode,
  ReframingDifficulty,
  ReframingContext,
  ReframingFeedbackTiming,
  ReframingInteractionMode,
} from '../components/ai/reframingPracticeConstants';

export interface ReframingSession {
  id: string;
  user_id: string;
  model: string;
  reasoning_effort: string;
  interaction_mode: ReframingInteractionMode;
  practice_mode: ReframingPracticeMode;
  difficulty: ReframingDifficulty;
  context: ReframingContext;
  feedback_timing: ReframingFeedbackTiming;
  session_length_questions: number | null;
  session_length_minutes: number | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  overall_score: number | null;
  exercises_completed: number;
  strongest_response: string | null;
  improvement_area: string | null;
  recommended_next: string | null;
  status: 'in_progress' | 'completed' | 'abandoned';
  created_at: string;
}

export async function getReframingVoiceToken(params: {
  practice_mode: ReframingPracticeMode;
  difficulty: ReframingDifficulty;
  context: ReframingContext;
  feedback_timing: ReframingFeedbackTiming;
  session_length_minutes?: number;
  interaction_mode: ReframingInteractionMode;
}): Promise<{ client_secret: string; practice_session_id: string | null; model: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reframing-practice-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to get voice token');
  }

  return res.json();
}

export async function sendReframingChatMessage(params: {
  message: string;
  conversation_history: { role: 'user' | 'assistant'; content: string }[];
  practice_mode: ReframingPracticeMode;
  difficulty: ReframingDifficulty;
  context: ReframingContext;
  feedback_timing: ReframingFeedbackTiming;
  session_length_questions?: number;
  practice_session_id?: string | null;
}): Promise<{ reply: string; model: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reframing-practice-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to send message');
  }

  return res.json();
}

export async function completeReframingSession(
  sessionId: string,
  data: {
    duration_seconds: number;
    transcript?: string;
    overall_score?: number;
    context_reframes_attempted?: number;
    meaning_reframes_attempted?: number;
    correct_identifications?: number;
    exercises_completed?: number;
    strongest_response?: string;
    improvement_area?: string;
    recommended_next?: string;
  }
): Promise<void> {
  const { error } = await (supabase as any)
    .from('reframing_practice_sessions')
    .update({
      ...data,
      ended_at: new Date().toISOString(),
      status: 'completed',
    })
    .eq('id', sessionId);

  if (error) throw error;
}

export async function createReframingSession(params: {
  practice_mode: ReframingPracticeMode;
  difficulty: ReframingDifficulty;
  context: ReframingContext;
  feedback_timing: ReframingFeedbackTiming;
  interaction_mode: ReframingInteractionMode;
  session_length_questions?: number;
  session_length_minutes?: number;
}): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: coach } = await supabase
    .from('coaches')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle();

  const { data, error } = await (supabase as any)
    .from('reframing_practice_sessions')
    .insert({
      user_id: user.id,
      coach_id: coach?.id || null,
      ...params,
      status: 'in_progress',
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

export async function fetchReframingSessions(limit = 20): Promise<ReframingSession[]> {
  const { data, error } = await (supabase as any)
    .from('reframing_practice_sessions')
    .select('*')
    .eq('status', 'completed')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []) as ReframingSession[];
}

export async function fetchReframingProgress() {
  const { data, error } = await (supabase as any)
    .from('reframing_student_progress')
    .select('*')
    .maybeSingle();

  if (error) return null;
  return data;
}
